import { memo, useMemo } from "react"

import { useTranslation } from "react-i18next"
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts"

type TrainingCompletionDatum = {
  moduleId: string
  label: string
  completedPct: number
  inProgressPct: number
  notStartedPct: number
  completedUsers: number
  inProgressUsers: number
  notStartedUsers: number
}

const CHART_HEIGHT = 320

const truncateLabel = (label: string) => {
  if (label.length <= 16) return label
  return `${label.slice(0, 16)}…`
}

const STATUS_SERIES = [
  {
    key: "completedPct",
    labelKey: "adminDashboard.training.status.completed",
    color: "hsl(var(--success-ink))",
  },
  {
    key: "inProgressPct",
    labelKey: "adminDashboard.training.status.inProgress",
    color: "hsl(var(--warning-ink))",
  },
  {
    key: "notStartedPct",
    labelKey: "adminDashboard.training.status.notStarted",
    color: "hsl(var(--muted-foreground))",
  },
] as const

const COUNT_KEYS: Record<(typeof STATUS_SERIES)[number]["key"], keyof TrainingCompletionDatum> = {
  completedPct: "completedUsers",
  inProgressPct: "inProgressUsers",
  notStartedPct: "notStartedUsers",
}

const TooltipContent = memo(function TooltipContent({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ payload?: TrainingCompletionDatum }>
}) {
  const { t } = useTranslation()
  if (!active || !payload?.length) return null
  const datum = payload[0]?.payload
  if (!datum) return null

  return (
    <div className="bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow">
      <div className="text-sm font-medium">{datum.label}</div>
      <div className="text-muted-foreground mt-2 space-y-1">
        {STATUS_SERIES.map((series) => {
          const value = datum[series.key as keyof TrainingCompletionDatum] as number
          const countKey = COUNT_KEYS[series.key]
          const countValue = datum[countKey] as number
          return (
            <div key={series.key} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <span className="size-2 rounded-full" style={{ backgroundColor: series.color }} />
                {t(series.labelKey)}
              </span>
              <span className="tabular-nums">
                {value.toFixed(1)}% · {countValue}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
})

type TrainingCompletionRadarProps = {
  data: TrainingCompletionDatum[]
}

export const TrainingCompletionRadar = memo(function TrainingCompletionRadar({
  data,
}: TrainingCompletionRadarProps) {
  const { t } = useTranslation()
  const labelById = useMemo(() => {
    return new Map(data.map((datum) => [datum.moduleId, datum.label]))
  }, [data])

  const renderTick = (props: Record<string, unknown>) => {
    const x = typeof props.x === "number" ? props.x : undefined
    const y = typeof props.y === "number" ? props.y : undefined
    if (x == null || y == null) return null
    const payload = props.payload as { value?: string | number } | undefined
    const rawValue = payload?.value
    const resolvedLabel =
      typeof rawValue === "string" ? (labelById.get(rawValue) ?? rawValue) : rawValue
    const rawAnchor = props.textAnchor as string | undefined
    const textAnchor: "start" | "middle" | "end" =
      rawAnchor === "start" || rawAnchor === "end" ? rawAnchor : "middle"

    return (
      <text fill="currentColor" fontSize={11} textAnchor={textAnchor} x={x} y={y}>
        {typeof resolvedLabel === "string" ? truncateLabel(resolvedLabel) : resolvedLabel}
      </text>
    )
  }

  return (
    <ResponsiveContainer height={CHART_HEIGHT} width="100%">
      <RadarChart cx="50%" cy="50%" data={data} outerRadius="80%">
        <PolarGrid className="stroke-muted" />
        <PolarAngleAxis dataKey="moduleId" tick={renderTick} />
        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} tickCount={6} />
        <Tooltip content={<TooltipContent />} />
        <Legend />
        {STATUS_SERIES.map((series) => (
          <Radar
            key={series.key}
            dataKey={series.key}
            dot={{ r: 3, fill: series.color, stroke: series.color, strokeWidth: 1 }}
            fill={series.color}
            fillOpacity={0.6}
            isAnimationActive={false}
            name={t(series.labelKey)}
            stroke={series.color}
            strokeWidth={3}
            style={{
              fill: series.color,
              stroke: series.color,
              fillOpacity: 0.6,
            }}
          />
        ))}
      </RadarChart>
    </ResponsiveContainer>
  )
})
